if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      get productForm() {
        return this.querySelector('form[is="product-form"');
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.resetFormState();
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateDelivery(variant);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          // --- MODIFIED BLOCK START ---
          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          const isSoldOut = addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true;

          // Find the input corresponding to the selected variant to check for data-preorder
          const selectedInput = html.querySelector('variant-selects input:checked, variant-selects option:checked');
          const isPreorder = selectedInput?.dataset.preorder === 'true';

          let buttonText = FoxTheme.variantStrings.addToCart;
          if (isSoldOut) {
            buttonText = FoxTheme.variantStrings.soldOut;
          } else if (isPreorder) {
            buttonText = FoxTheme.variantStrings.preorder;
          }

          this.toggleAddButton(isSoldOut, buttonText);
          // --- MODIFIED BLOCK END ---

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: this.currentVariant,
              },
            })
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }


      updateDelivery(variant) {
        if (!variant?.id) {
          return;
        }

        const variantElement = this.querySelector(`[data-variant="${variant.id}"]`);
        const shippingMessage = variantElement?.dataset.shipping;

        if (typeof shippingMessage !== 'string') {
          return;
        }

        const destination = this.querySelector('.product__inventory-text span');
        if (destination) {
          destination.innerText = shippingMessage;
        }

        const leadTimeInput = this.querySelector('[name="properties[Lead time]"]');
        if (leadTimeInput) {
          leadTimeInput.value = shippingMessage;
        }
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          // --- CHANGED: Use text if provided, else fallback to addToCart ---
          addButtonText.innerHTML = text || FoxTheme.variantStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }
    }
  );
}

if (!customElements.get('product-promotion-alert')) {
  customElements.define(
    'product-promotion-alert',
    class ProductPromotionAlert extends HTMLElement {
      constructor() {
        super();

        this.abortController = new AbortController();
        this.buttonEl = this.querySelector('button');
        this.buttonEl.addEventListener('click', this.onClick.bind(this), {
          signal: this.abortController.signal,
        });
      }

      onClick(evt) {
        evt.preventDefault();

        this.closest('.product__block').classList.add('hidden');
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    }
  );
}

if (!customElements.get('delivery-countdown')) {
  customElements.define('delivery-countdown', class DeliveryCountdown extends HTMLElement {
    constructor() {
      super();
      this.timer = null;
      this.cutoff = this.dataset.cutoff || '3:00pm';
      this.countdownEl = this.querySelector('.countdown-timer');
      this.textContainer = this.querySelector('.delivery-countdown__text') || this.querySelector('p');

      // State tracking
      this.dateTextNode = null;
      this.originalTemplate = null;
      this.lastRenderedLabel = '';
      this.isInitialized = false;

      // Cache for the specific number elements
      this.els = { hours: null, minutes: null, seconds: null };
    }

    connectedCallback() {
      // 1. Prepare the HTML structure once
      this.initTimerStructure();

      // 2. Run immediately
      this.updateCountdown();

      // 3. Start Interval
      this.timer = setInterval(() => this.updateCountdown(), 1000);
    }

    disconnectedCallback() {
      if (this.timer) clearInterval(this.timer);
    }

    // --- HELPER: Create separate spans for the numbers ---
    initTimerStructure() {
      if (!this.countdownEl || this.isInitialized) return;

      // We overwrite the textContent with a structured HTML template
      // This isolates the numbers so they don't shake the text
      this.countdownEl.innerHTML = `
        <span class="dc-num dc-h">--</span> hours, 
        <span class="dc-num dc-m">--</span> minutes
      `;

      // Cache the elements so we don't querySelector every second
      this.els.hours = this.countdownEl.querySelector('.dc-h');
      this.els.minutes = this.countdownEl.querySelector('.dc-m');
      this.els.seconds = this.countdownEl.querySelector('.dc-s');

      this.isInitialized = true;
    }

    parseCutoffTime() {
      const s = (this.cutoff || '').trim().toLowerCase();
      const m = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/);
      if (!m) return null;

      let h = parseInt(m[1], 10);
      const min = m[2] ? parseInt(m[2], 10) : 0;
      const period = m[3];

      if (period === 'pm' && h !== 12) h += 12;
      if (period === 'am' && h === 12) h = 0;

      return { hour: h, minute: min };
    }

    getDeliveryDate(now) {
      const cutoff = this.parseCutoffTime();
      if (!cutoff) return null;

      const todayCutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate(), cutoff.hour, cutoff.minute, 0, 0);
      const isBeforeCutoff = now <= todayCutoff;
      const day = now.getDay();
      const delivery = new Date(now);

      // RULE: Fri after cutoff, Sat, Sun → Tuesday
      if ((day === 5 && !isBeforeCutoff) || day === 6 || day === 0) {
        let daysToTue = (2 - day + 7) % 7;
        if (daysToTue === 0) daysToTue = 7;
        delivery.setDate(now.getDate() + daysToTue);
        return delivery;
      }

      // Mon–Fri before cutoff → next day
      if (day >= 1 && day <= 5 && isBeforeCutoff) {
        delivery.setDate(now.getDate() + 1);
        return delivery;
      }

      // Mon–Thu after cutoff → +2 days
      if (day >= 1 && day <= 4 && !isBeforeCutoff) {
        delivery.setDate(now.getDate() + 2);
        return delivery;
      }

      return delivery;
    }

    findAndCacheTextNode() {
      if (!this.textContainer) return;
      const dayRegex = /\b(today|tomorrow|sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b/i;

      const findNode = (node) => {
        node.childNodes.forEach(child => {
          if (this.dateTextNode) return;
          if (child.nodeType === Node.TEXT_NODE) {
            if (dayRegex.test(child.nodeValue)) {
              this.dateTextNode = child;
              this.originalTemplate = child.nodeValue;
            }
          } else if (child.nodeType === Node.ELEMENT_NODE) {
            if (child.classList && child.classList.contains('countdown-timer')) return;
            findNode(child);
          }
        });
      };
      findNode(this.textContainer);
    }

    updateCountdown() {
      const cutoff = this.parseCutoffTime();
      if (!cutoff || !this.countdownEl) return;

      const now = new Date();
      let nextCutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate(), cutoff.hour, cutoff.minute, 0, 0);

      if (now > nextCutoff) nextCutoff.setDate(nextCutoff.getDate() + 1);

      const diffMs = Math.max(0, nextCutoff - now);
      const totalSeconds = Math.floor(diffMs / 1000);

      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      // --- 1. UPDATE NUMBERS ONLY (No Layout Shift) ---
      // We only update the specific <span> content, not the whole line
      if (this.isInitialized) {
        this.els.hours.textContent = hours; // Or hours.toString().padStart(2, '0') if you prefer 03 vs 3
        this.els.minutes.textContent = minutes.toString().padStart(2, '0');
      }

      // --- 2. DATE REPLACEMENT LOGIC ---
      if (!this.dateTextNode) this.findAndCacheTextNode();
      if (!this.dateTextNode || !this.originalTemplate) return;

      const deliveryDate = this.getDeliveryDate(now);
      const newLabel = `${deliveryDate.toLocaleDateString('en-US', { weekday: 'long' })}, ${deliveryDate.toLocaleDateString('en-US', { month: 'long' })} ${deliveryDate.getDate()}`;

      if (newLabel !== this.lastRenderedLabel) {
        const dayRegex = /\b(today|tomorrow|sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b/i;
        this.dateTextNode.nodeValue = this.originalTemplate.replace(dayRegex, newLabel);
        this.lastRenderedLabel = newLabel;
      }
    }
  });
}